"use client";

import { useCallback, useState, type ReactNode } from "react";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { InviteUser, type InviteUserPayload } from "@/components/user-management/invite-user";
import { showInviteUsersResultToast } from "@/components/user-management/invite-user-toast";
import { TeamMembersSummary } from "@/components/user-management/team-members-summary";
import { StripeSection } from "@/components/manage-workspace-modal/sections/integrations/stripe-section";
import { ManageSuppliersSection } from "@/components/manage-workspace-modal/sections/manage-suppliers/manage-suppliers-section";
import { ManageCustomerPaymentMethodsSection } from "@/components/manage-workspace-modal/sections/manage-customer-payment-methods/manage-customer-payment-methods-section";
import { ManageSupplierPaymentMethodsSection } from "@/components/manage-workspace-modal/sections/manage-supplier-payment-methods/manage-supplier-payment-methods-section";

function WizardSkipHint({ children }: { children: ReactNode }) {
  return (
    <p className="text-sm text-muted-foreground">{children}</p>
  );
}

export function WorkspaceTeamStep() {
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);

  const { doAction: inviteUsers } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => (values: InviteUserPayload) =>
        sdk.inviteUser({
          emails: values.emails,
          permissions: [],
          access: values.access,
        }),
      failureMessage: "Failed to send invitations",
      throwError: true,
    }),
    []
  );

  const handleInvite = useCallback(
    async (values: InviteUserPayload) => {
      const result = await inviteUsers(values);
      if (!result) return result;
      showInviteUsersResultToast(result);
      setSummaryRefreshKey((current) => current + 1);
      return result;
    },
    [inviteUsers]
  );

  return (
    <div className="space-y-4">
      <TeamMembersSummary refreshKey={summaryRefreshKey} />
      <WizardSkipHint>
        Invite colleagues who will help manage quotes and bookings. Enter
        multiple email addresses separated by commas. You can skip this and
        invite people later from Manage Workspace.
      </WizardSkipHint>
      <InviteUser onInvite={handleInvite} invitesRefreshKey={summaryRefreshKey} />
    </div>
  );
}

export function WorkspaceStripeStep() {
  return (
    <div className="space-y-4">
      <WizardSkipHint>
        Connect Stripe to accept card payments on quotes and bookings. Card
        payments won&apos;t work until Stripe is connected — you can set this up
        later.
      </WizardSkipHint>
      <div className="max-h-[50vh] overflow-y-auto pr-1">
        <StripeSection />
      </div>
    </div>
  );
}

export function WorkspaceSuppliersStep() {
  return (
    <div className="space-y-4">
      <WizardSkipHint>
        Add travel suppliers you book through. Start with your most-used
        supplier — you can add more anytime.
      </WizardSkipHint>
      <div className="max-h-[50vh] overflow-y-auto pr-1">
        <ManageSuppliersSection layout="wizard" />
      </div>
    </div>
  );
}

export function WorkspaceCustomerPaymentMethodsStep() {
  return (
    <div className="space-y-4">
      <WizardSkipHint>
        Set up how customers pay you — e.g. bank transfer, card, or cash. These
        appear when recording booking payments.
      </WizardSkipHint>
      <div className="max-h-[50vh] overflow-y-auto pr-1">
        <ManageCustomerPaymentMethodsSection layout="wizard" />
      </div>
    </div>
  );
}

export function WorkspaceSupplierPaymentMethodsStep() {
  return (
    <div className="space-y-4">
      <WizardSkipHint>
        Configure how you pay suppliers — e.g. BACS, company card, or trade
        account.
      </WizardSkipHint>
      <div className="max-h-[50vh] overflow-y-auto pr-1">
        <ManageSupplierPaymentMethodsSection layout="wizard" />
      </div>
    </div>
  );
}

